// ORBAT-velger: ren, DOM-fri modell som kobler kartplassering til ORBAT.
//
// Hvorfor: i dag får en enhet som slippes på kartet en generert id
// (`u-<tidsstempel>`), og er dermed IKKE koblet til styrkestrukturen. Kartmanøver
// fase 2 krever det motsatte — enhetens id MÅ være en ORBAT-unit-id, slik at
// popup, ordre-generering og stridsevne kan slå opp den faktiske enheten.
//
// Denne fila er modellen UI-et drives av: en flat, søkbar katalog utledet fra
// `ORBAT_SEED`, søk/rangering, echelon-filter, «allerede plassert»-håndtering og
// oppslag id → ORBAT-enhet. Ingen DOM, ingen maplibre, ingen import fra
// `tools/units.ts` — alt er testbart i node.
import {
  ORBAT_SEED, ORBAT_ECHELON_LABEL, ORBAT_ECHELON_ABBR, orbatEchelonRank,
  type OrbatUnit, type OrbatEchelon,
} from '@skade/contracts';

// ── Katalog ─────────────────────────────────────────────────────────────────

/** Én rad i den flate, søkbare ORBAT-katalogen — alt UI-et trenger per enhet. */
export interface OrbatKatalogPost {
  /** ORBAT-unit-id. Dette blir enhetens id på kartet (ingen `u-<tid>`). */
  id: string;
  /** Fullt navn slik det står i ORBAT. */
  navn: string;
  /** Entydig kortform til kartetikett (11 enheter heter «Områdestruktur»). */
  kortNavn: string;
  echelon: OrbatEchelon;
  /** Lesbart echelon-navn («Distrikt/Formasjon»). */
  echelonNavn: string;
  /** Forkortelse til tett UI («DIST», «KP»). */
  echelonKort: string;
  /** APP-6/2525 SIDC-streng som skal tegnes for enheten. */
  sidc: string;
  /** Foreldrekjeden som navn, rot → nærmeste forelder (uten enheten selv). */
  sti: string[];
  /** Full sti inkludert enheten: «Heimevernet / HV-02 … / Oslo Sentrum». */
  stiTekst: string;
  parentId?: string;
  /** 0 for rotnoder, +1 per nivå ned. */
  dybde: number;
  /** Standkvarter (fritekst) hvis satt i ORBAT. */
  hq?: string;
  /** Materialisert ltree-sti fra ORBAT («FOH.Heimevernet.HV-02»). */
  ltreeSti?: string;
  /** Normalisert (foldet) søketekst for navnet — se `normaliserSok`. */
  sokNavn: string;
  /** Normalisert (foldet) søketekst for foreldrekjeden. */
  sokSti: string;
}

/** Skilletegn mellom stileddene i `stiTekst`. */
export const STI_SKILLE = ' / ';

/**
 * Normaliser tekst for søk: små bokstaver, komprimert whitespace, og norske/
 * svenske tegn foldet til ASCII (æ→ae, ø→o, å→a, ä→a, ö→o, é→e).
 *
 * Foldingen er tosidig nyttig: «møre» og «more» treffer begge «Søre Sunnmøre»,
 * og «haeren» treffer «Hæren» (som er slik id-en faktisk staves i seeden).
 * Både katalogtekst og søkestreng foldes, så matching skjer i ETT tegnrom —
 * indeksene (og dermed rangeringen) er derfor alltid konsistente.
 */
export function normaliserSok(s: string): string {
  return s
    .toLowerCase()
    // NFD + fjern kombinerende tegn: å→a, ä→a, ö→o, é→e, ü→u …
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    // Egne bokstaver uten dekomponering:
    .replace(/æ/g, 'ae').replace(/ø/g, 'o').replace(/ð/g, 'd').replace(/þ/g, 'th')
    .replace(/ß/g, 'ss')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Første token i et navn («HV-02 Oslo og Akershus» → «HV-02»). */
function forsteToken(navn: string): string {
  return navn.split(' ')[0] ?? navn;
}

/**
 * Gjør ORBAT-treet om til en flat, søkbar katalog.
 *
 * Rekkefølgen er seedens egen (hierarkisk byggerekkefølge), som gir et lesbart
 * tre-ish resultat når det ikke søkes — og er deterministisk.
 * Robust mot manglende foreldre og sykler i parent-kjeden.
 */
export function byggOrbatKatalog(enheter: readonly OrbatUnit[] = ORBAT_SEED): OrbatKatalogPost[] {
  const byId = new Map<string, OrbatUnit>();
  for (const u of enheter) byId.set(u.id, u);

  // Hvor mange enheter deler samme navn? Styrer om kortNavn må disambigueres.
  const navnAntall = new Map<string, number>();
  for (const u of enheter) navnAntall.set(u.name, (navnAntall.get(u.name) ?? 0) + 1);

  /** Foreldrekjede som navn, rot → nærmeste forelder. Stopper ved syklus. */
  const forfedre = (u: OrbatUnit): string[] => {
    const kjede: string[] = [];
    const sett = new Set<string>([u.id]);
    let pid = u.parentId;
    while (pid && !sett.has(pid)) {
      const p = byId.get(pid);
      if (!p) break;
      kjede.unshift(p.name);
      sett.add(pid);
      pid = p.parentId;
    }
    return kjede;
  };

  const poster: OrbatKatalogPost[] = enheter.map((u) => {
    const sti = forfedre(u);
    const flere = (navnAntall.get(u.name) ?? 0) > 1;
    const forelder = u.parentId ? byId.get(u.parentId) : undefined;
    const kortNavn = flere && forelder ? `${forsteToken(forelder.name)} ${u.name}` : u.name;
    return {
      id: u.id,
      navn: u.name,
      kortNavn,
      echelon: u.echelon,
      echelonNavn: ORBAT_ECHELON_LABEL[u.echelon] ?? u.echelon,
      echelonKort: ORBAT_ECHELON_ABBR[u.echelon] ?? u.echelon,
      sidc: u.sidc ?? '',
      sti,
      stiTekst: [...sti, u.name].join(STI_SKILLE),
      parentId: u.parentId,
      dybde: sti.length,
      hq: u.hq,
      ltreeSti: u.path,
      sokNavn: normaliserSok(u.name),
      sokSti: normaliserSok(sti.join(' ')),
    };
  });

  // Sikkerhetsnett: dersom disambigueringen over ikke ble entydig (to foreldre med
  // samme første token), heng på id-en. Katalogen skal ALDRI ha to like kortNavn.
  const kortAntall = new Map<string, number>();
  for (const p of poster) kortAntall.set(p.kortNavn, (kortAntall.get(p.kortNavn) ?? 0) + 1);
  for (const p of poster) {
    if ((kortAntall.get(p.kortNavn) ?? 0) > 1) p.kortNavn = `${p.kortNavn} (${p.id})`;
  }

  return poster;
}

// ── Søk og rangering ────────────────────────────────────────────────────────

/** Hvor treffet ble funnet — styrer rangeringen (lavere score = bedre). */
export type OrbatTreffType =
  | 'navn-eksakt' // hele navnet er søkestrengen
  | 'navn-prefiks' // navnet starter med søkestrengen
  | 'navn-ord' // søkestrengen starter et ord i navnet
  | 'navn-delvis' // søkestrengen ligger midt i et ord i navnet
  | 'sti-ord' // søkestrengen starter et ord i foreldrekjeden
  | 'sti-delvis' // søkestrengen ligger midt i et ord i foreldrekjeden
  | 'alle'; // tomt søk — ingen rangering, katalogrekkefølge

/** Rangering: lavere tall vinner. Prefiks-treff slår midt-i-ord-treff, og
 * navne-treff slår sti-treff (stien er kontekst, ikke det du søkte etter). */
export const TREFF_SCORE: Record<OrbatTreffType, number> = {
  'navn-eksakt': 0,
  'navn-prefiks': 1,
  'navn-ord': 2,
  'navn-delvis': 3,
  'sti-ord': 4,
  'sti-delvis': 5,
  alle: 6,
};

export interface OrbatSokTreff {
  post: OrbatKatalogPost;
  treff: OrbatTreffType;
  /** `TREFF_SCORE[treff]` — eksponert slik at UI kan gruppere uten oppslag. */
  score: number;
  /** Er enheten allerede plassert på kartet? (markering, ikke nødvendigvis skjul) */
  erPlassert: boolean;
}

export interface OrbatSokValg {
  /** Fritekst. Tom/blank streng = alle (katalogrekkefølge). */
  q?: string;
  /** Bare disse echelonene. Tom liste / utelatt = alle. */
  echelon?: OrbatEchelon | readonly OrbatEchelon[];
  /** Id-er som allerede står på kartet (markeres med `erPlassert`). */
  plasserteIder?: Iterable<string>;
  /** Utelat allerede plasserte helt (en tropp kan ikke være på to punkter). */
  skjulPlasserte?: boolean;
  /** Maks antall treff (etter sortering). */
  maks?: number;
}

/** Er `i` starten av et ord i `tekst`? (posisjon 0, eller ikke-alfanumerisk foran) */
function erOrdstart(tekst: string, i: number): boolean {
  if (i <= 0) return true;
  return !/[a-z0-9]/.test(tekst[i - 1]!);
}

/** Klassifiser treffet av `q` i én katalogpost. `null` = ingen treff. */
function klassifiser(post: OrbatKatalogPost, q: string): OrbatTreffType | null {
  if (post.sokNavn === q) return 'navn-eksakt';
  const iNavn = post.sokNavn.indexOf(q);
  if (iNavn === 0) return 'navn-prefiks';
  if (iNavn > 0) return erOrdstart(post.sokNavn, iNavn) ? 'navn-ord' : 'navn-delvis';
  const iSti = post.sokSti.indexOf(q);
  if (iSti >= 0) return erOrdstart(post.sokSti, iSti) ? 'sti-ord' : 'sti-delvis';
  return null;
}

function echelonSett(e: OrbatSokValg['echelon']): Set<OrbatEchelon> | null {
  if (!e) return null;
  const liste = Array.isArray(e) ? e : [e as OrbatEchelon];
  return liste.length ? new Set(liste) : null;
}

/**
 * Søk/filtrer katalogen. Deterministisk: samme input → samme rekkefølge.
 *
 * Sortering ved likt score: høyest echelon først (en operatør vil se distriktet
 * før lagene under det), deretter navn, deretter id — alle tre er totale, så
 * rekkefølgen er stabil uavhengig av sorteringsalgoritme.
 */
export function sokOrbat(
  katalog: readonly OrbatKatalogPost[],
  valg: OrbatSokValg = {},
): OrbatSokTreff[] {
  const q = normaliserSok(valg.q ?? '');
  const ech = echelonSett(valg.echelon);
  const plasserte = new Set<string>(valg.plasserteIder ?? []);

  const treff: OrbatSokTreff[] = [];
  for (const post of katalog) {
    if (ech && !ech.has(post.echelon)) continue;
    const erPlassert = plasserte.has(post.id);
    if (erPlassert && valg.skjulPlasserte) continue;
    const type = q ? klassifiser(post, q) : 'alle';
    if (!type) continue;
    treff.push({ post, treff: type, score: TREFF_SCORE[type], erPlassert });
  }

  // Tomt søk: behold katalogrekkefølgen (hierarkisk) — den er allerede lesbar.
  if (q) {
    treff.sort((a, b) => (
      a.score - b.score
      || orbatEchelonRank(b.post.echelon) - orbatEchelonRank(a.post.echelon)
      || a.post.navn.localeCompare(b.post.navn, 'nb')
      || (a.post.id < b.post.id ? -1 : a.post.id > b.post.id ? 1 : 0)
    ));
  }

  return valg.maks !== undefined && valg.maks >= 0 ? treff.slice(0, valg.maks) : treff;
}

/** Bare denne/disse echelonene (tynn innpakning rundt `sokOrbat`). */
export function filtrerEchelon(
  katalog: readonly OrbatKatalogPost[],
  echelon: OrbatEchelon | readonly OrbatEchelon[],
): OrbatKatalogPost[] {
  return sokOrbat(katalog, { echelon }).map((t) => t.post);
}

/** Echelonene som faktisk finnes i katalogen, høyest først, med antall.
 * Driver echelon-filteret i UI uten at UI trenger å kjenne ORBAT-taksonomien. */
export function echelonAlternativer(
  katalog: readonly OrbatKatalogPost[],
): Array<{ echelon: OrbatEchelon; navn: string; kort: string; antall: number }> {
  const antall = new Map<OrbatEchelon, number>();
  for (const p of katalog) antall.set(p.echelon, (antall.get(p.echelon) ?? 0) + 1);
  return [...antall.entries()]
    .map(([echelon, n]) => ({
      echelon,
      navn: ORBAT_ECHELON_LABEL[echelon] ?? echelon,
      kort: ORBAT_ECHELON_ABBR[echelon] ?? echelon,
      antall: n,
    }))
    .sort((a, b) => orbatEchelonRank(b.echelon) - orbatEchelonRank(a.echelon)
      || a.echelon.localeCompare(b.echelon));
}

// ── Oppslag id → ORBAT-enhet ────────────────────────────────────────────────

/** Slå opp den rå ORBAT-enheten (til popup / ordre-generering). */
export function orbatEnhet(
  id: string,
  enheter: readonly OrbatUnit[] = ORBAT_SEED,
): OrbatUnit | undefined {
  return enheter.find((u) => u.id === id);
}

/** Slå opp katalogposten (navn + sti + echelon-tekst ferdig utledet). */
export function orbatKatalogPost(
  id: string,
  katalog: readonly OrbatKatalogPost[],
): OrbatKatalogPost | undefined {
  return katalog.find((p) => p.id === id);
}

/** Er id-en en ekte ORBAT-enhet? (skiller ORBAT-koblede enheter fra `u-<tid>`) */
export function erOrbatId(id: string, katalog: readonly OrbatKatalogPost[]): boolean {
  return katalog.some((p) => p.id === id);
}

// ── Plassering på kartet ────────────────────────────────────────────────────

/**
 * Minimal, egen form for en enhet som skal plasseres på kartet. Feltnavnene er
 * med vilje identiske med `PlacedUnit` i `tools/units.ts`, men vi importerer
 * IKKE derfra — velgeren skal være DOM-fri og uavhengig av kartverktøyet.
 * Innkoblingen mapper dette rett over.
 */
export interface OrbatPlassering {
  /** = ORBAT-unit-id. Dette er hele poenget: kartenheten ER ORBAT-enheten. */
  id: string;
  sidc: string;
  lon: number;
  lat: number;
  label: string;
}

/**
 * Lag en plasserings-struktur for en ORBAT-enhet i et gitt punkt.
 * `null` hvis id-en ikke finnes i katalogen (ingen stille oppfinning av enheter).
 */
export function lagOrbatPlassering(
  id: string,
  lon: number,
  lat: number,
  katalog: readonly OrbatKatalogPost[],
): OrbatPlassering | null {
  const post = orbatKatalogPost(id, katalog);
  if (!post) return null;
  return { id: post.id, sidc: post.sidc, lon, lat, label: post.kortNavn };
}
